import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, Pencil, Plus, RefreshCw } from 'lucide-react';
import { fetchConsoleMapping } from '@/hooks/useAddressValidation';
import type { DmpConsoleMappingResponse, DmpConsoleMappingRow } from '@uniticket/shared';

// N39 (décisions du user du 08-10) — le bloc « Table de mapping » de la fiche d'un JVID dans la console DMP :
// ce que CHAQUE opérateur déclare pour cette adresse, une ligne par opérateur, sans ticket derrière.
//
// Il se LIT par tous ceux qui ouvrent la console (validateurs et comptes Build compris). Les boutons « Modifier »
// et « Créer » n'apparaissent que si l'appelant passe `onEdit` — la PAGE console, pour un admin ; le tiroir ouvert
// depuis un ticket ne passe rien. Cacher le bouton est un confort : le serveur garde l'écriture de son côté.
//
// Une lecture qui échoue se DIT (avec de quoi réessayer) : un bloc vide se lirait « aucun opérateur n'a rien
// déclaré », et c'est précisément l'information que l'on vient chercher ici.

// Même format que les autres écrans d'admin : belge, quel que soit le navigateur.
const fmt = (iso: string) => new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' });

const line = (r: DmpConsoleMappingRow) =>
  [r.streetName, r.houseNumber, r.houseNumberExtension && r.houseNumberExtension !== r.houseNumber ? r.houseNumberExtension : null]
    .filter(Boolean).join(' ') || '—';

export function ConsoleMappingBlock({
  jvid,
  onEdit,
}: {
  jvid: number;
  /** Ouvre le formulaire sur une ligne — `null` = créer la déclaration d'un opérateur qui n'en a pas. */
  onEdit?: (mapping: DmpConsoleMappingResponse, row: DmpConsoleMappingRow | null) => void;
}) {
  const { t } = useTranslation(['tickets']);
  const [mapping, setMapping] = useState<DmpConsoleMappingResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setMapping((await fetchConsoleMapping(jvid)).data);
    } catch {
      setMapping(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [jvid]);
  useEffect(() => { void load(); }, [load]);

  // Un opérateur au moins n'a pas encore de déclaration : c'est pour lui que « Créer » a un sens.
  const free = mapping ? mapping.operators.filter((o) => !mapping.rows.some((r) => r.operatorId === o.operatorId)) : [];

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {t('tickets:dmpLookup.mapping.title')}
          {mapping && <span className="ml-1 font-normal normal-case text-slate-400">· {t('tickets:dmpLookup.mapping.count', { count: mapping.rows.length })}</span>}
        </p>
        {onEdit && mapping && mapping.writeEnabled && free.length > 0 && (
          <button
            type="button"
            onClick={() => onEdit(mapping, null)}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-brand-rose/40 hover:text-brand-rose"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('tickets:dmpLookup.mapping.createBtn')}
          </button>
        )}
      </div>

      {loading ? (
        <p className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-3 text-[11px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('tickets:dmpLookup.mapping.loading')}
        </p>
      ) : failed || !mapping ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5" role="alert">
          <p className="flex items-start gap-1.5 text-[11px] font-medium text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t('tickets:dmpLookup.mapping.error')}
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t('tickets:dmpLookup.mapping.retry')}
          </button>
        </div>
      ) : mapping.rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 px-3 py-3 text-center text-[11px] text-slate-500">
          {t('tickets:dmpLookup.mapping.none')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-xs">
            <tbody>
              {mapping.rows.map((r) => (
                <tr key={r.operatorId} className="border-t border-slate-100 first:border-t-0">
                  <td className="whitespace-nowrap px-3 py-2 font-semibold text-slate-700">{r.operatorLabel}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {line(r)}
                    {r.busNumber?.trim() ? <span className="text-slate-500"> · {t('tickets:dmpLookup.mapping.box', { bus: r.busNumber })}</span> : null}
                    <span className="text-slate-500"> · {[r.postalCode, r.city ?? r.municipality].filter(Boolean).join(' ')}</span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {t('tickets:dmpLookup.mapping.modified', { who: r.modifiedBy || '—', when: r.updatedAt ? fmt(r.updatedAt) : '—' })}
                  </td>
                  {onEdit && (
                    <td className="w-px whitespace-nowrap px-2 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => onEdit(mapping, r)}
                        disabled={!mapping.writeEnabled}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-brand-rose/40 hover:text-brand-rose disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Pencil className="h-3 w-3" />
                        {t('tickets:dmpLookup.mapping.editBtn')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* L'écriture est coupée ici : le dire à celui qui pourrait écrire, plutôt que de le laisser buter sur un refus. */}
      {onEdit && mapping && !mapping.writeEnabled && (
        <p className="mt-1.5 text-[11px] text-amber-700">{t('tickets:dmpLookup.mapping.writeOff')}</p>
      )}
      <p className="mt-1.5 text-[10px] text-slate-400">{t('tickets:dmpLookup.mapping.hint')}</p>
    </div>
  );
}
